import React, { FC, useCallback, useState } from 'react';
import { format, subDays, differenceInDays } from 'date-fns';
import { joiResolver } from '@hookform/resolvers/joi';
import { FormProvider, useForm, UseFormReturn } from 'react-hook-form';
import { Button } from '../../components/Button';
import { TabSettings, TabsWithPerms } from '../../components/TabsWithPerms';
import { BenefitGroup } from './BenefitGroups';
import { GroupContacts } from './Contacts';
import { GroupDetails } from './GroupDetails';
import { GroupFileUpload } from './Logos';
import { publishSchema, terminateValidation } from '../../utils/groups/validationSchema';
import WithPermission, { PermMode } from '../../components/WithPermission';
import Modal from '../../components/Modal/NewModal';
import { useGroupContext } from '../../context/GroupContext';
import { Select, DatePicker, TextArea } from '../../components/ReactHookForm';
import { terminationReason } from '../../data';
import { TransformedGroupPackage, TransformedGroupsPackage } from '../../@types/Groups';
import { Nullable } from '../../@types/common';
import {
  TabType,
  GroupsFormProps,
  TerminationData,
  CancelChangesModalProps,
  DeleteDraftModalProps,
  TerminateModalProps,
  ConfirmPublishModalProps
} from '../../@types/ManageGroups';
import { GroupType } from '../../api/api';

enum ModalType {
  CANCEL = 'cancel',
  DRAFT_DELETE = 'draftDelete',
  PUBLISH = 'publish',
  TERMIANTE = 'terminate'
}

const tabsSettings: TabSettings = {
  [TabType.GroupDetails]: {
    perms: ['GroupRead', 'DraftRead', 'GroupDetailsRead'],
    permsMode: PermMode.ANY,
    label: TabType.GroupDetails,
    component: <GroupDetails />
  },
  [TabType.BenefitGroups]: {
    perms: ['GroupRead', 'DraftRead', 'GroupBenefitSponsorRead'],
    permsMode: PermMode.ALL,
    label: TabType.BenefitGroups,
    component: <BenefitGroup />
  },
  [TabType.Contacts]: {
    perms: ['GroupRead', 'DraftRead', 'GroupContactsRead'],
    permsMode: PermMode.ALL,
    label: TabType.Contacts,
    component: <GroupContacts />
  },
  [TabType.Logos]: {
    perms: ['GroupRead', 'DraftRead', 'GroupLogoRead'],
    permsMode: PermMode.ALL,
    label: TabType.Logos,
    component: <GroupFileUpload />
  }
};

export const GroupsForm: FC<GroupsFormProps> = ({
  defaultValues,
  type,
  onDraftDelete,
  onSaveAsDraft,
  onPublish
}: GroupsFormProps) => {
  const [activeModalType, setActiveModalType] = useState<ModalType | null>(null);
  const handleCloseModal = useCallback(() => setActiveModalType(null), []);

  const { selectedBenefitIdx, setBenefitIdx, activeTab, setActiveTab } = useGroupContext();

  const methods: UseFormReturn<TransformedGroupsPackage> = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues,
    resolver: joiResolver(publishSchema)
  });

  const {
    getValues,
    setValue,
    formState: { isValid },
    reset,
    resetField,
    trigger
  } = methods;

  const handleCancel = useCallback(() => {
    reset();
    handleCloseModal();
  }, [handleCloseModal, reset]);

  const handleDeleteDraft = useCallback(() => {
    onDraftDelete();
    handleCloseModal();
  }, [onDraftDelete, handleCloseModal]);

  const handleDeletePlan = useCallback(() => {
    if (selectedBenefitIdx !== null) {
      resetField(`groups.${selectedBenefitIdx}`, { defaultValue: null });
      setValue(
        'groups',
        getValues('groups').filter((_: TransformedGroupPackage, idx: number) => idx !== selectedBenefitIdx)
      );
      setBenefitIdx(null);
    }
  }, [selectedBenefitIdx, resetField, setValue, getValues, setBenefitIdx]);

  const handleTerminate = useCallback(
    ({ end_dt, terminateReason, customReason }: TerminationData) => {
      setValue('end_dt', end_dt);
      setValue('terminateReason', terminateReason === 'Other' ? customReason : terminateReason);
      getValues('groups').forEach((group) => {
        if (!group.end_dt || differenceInDays(new Date(group.end_dt), new Date(end_dt)) >= 60) {
          group.end_dt = end_dt;
        }
      });
      onPublish(getValues());
    },
    [setValue, getValues, onPublish]
  );

  const handleSaveAsDraft = useCallback(async () => {
    if (await trigger(['planNumber', 'name'])) onSaveAsDraft(getValues());
  }, [onSaveAsDraft, getValues, trigger]);

  const handleSave = useCallback(() => {
    onPublish(getValues());
    handleCloseModal();
  }, [onPublish, getValues, handleCloseModal]);

  const modals: Record<ModalType, React.ReactNode> = {
    [ModalType.CANCEL]: <CancelChangesModal onSubmit={handleCancel} onCancel={handleCloseModal} />,
    [ModalType.DRAFT_DELETE]: (
      <DeleteDraftModal onSubmit={handleDeleteDraft} onCancel={handleCloseModal} groupName={getValues('name')} />
    ),
    [ModalType.PUBLISH]: (
      <ConfirmPublishModal onSubmit={handleSave} onCancel={handleCloseModal} groupName={getValues('name')} />
    ),
    [ModalType.TERMIANTE]: (
      <TerminateModal
        onSubmit={handleTerminate}
        onCancel={handleCloseModal}
        data={getValues(['name', 'end_dt', 'groups'])}
      />
    )
  };

  return (
    <>
      <div className="flex flex-col flex-1">
        <FormProvider {...methods}>
          <form className="flex-1">
            <TabsWithPerms tabsSettings={tabsSettings} onTabChange={setActiveTab} />
          </form>
        </FormProvider>
        <div className="flex justify-between pt-3 border-t-1">
          <div>
            {type === GroupType.ACTIVE && (
              <WithPermission requiredPerms={['GroupRead', 'GroupWrite']}>
                <Button
                  onClick={() => setActiveModalType(ModalType.TERMIANTE)}
                  kind="secondary"
                  className="rounded-3xl px-6"
                >
                  Terminate this group
                </Button>
              </WithPermission>
            )}
            {type === GroupType.DRAFT && (
              <>
                {activeTab === TabType.BenefitGroups && selectedBenefitIdx !== null && (
                  <WithPermission requiredPerms={['DraftWrite', 'DraftCreate']}>
                    <Button onClick={handleDeletePlan} kind="secondary" className="rounded-3xl px-6">
                      Delete this plan
                    </Button>
                  </WithPermission>
                )}
                {selectedBenefitIdx === null && (
                  <WithPermission requiredPerms={['DraftWrite']}>
                    <Button
                      onClick={() => setActiveModalType(ModalType.DRAFT_DELETE)}
                      kind="secondary"
                      className="rounded-3xl px-6"
                    >
                      Delete this draft
                    </Button>
                  </WithPermission>
                )}
              </>
            )}
          </div>
          <div className="space-x-4">
            <WithPermission requiredPerms={['GroupRead', 'DraftRead']}>
              <Button
                onClick={() => setActiveModalType(ModalType.CANCEL)}
                kind="secondary"
                className="rounded-3xl px-6"
              >
                Cancel
              </Button>
            </WithPermission>
            <WithPermission requiredPerms={['GroupCreate']}>
              <Button
                onClick={() => setActiveModalType(ModalType.PUBLISH)}
                disabled={!isValid}
                className="rounded-3xl px-6"
                tooltip="Once all of the required fields are enabled, the Publish button becomes enabled."
              >
                Publish
              </Button>
            </WithPermission>
            {type !== GroupType.DRAFT && (
              <WithPermission requiredPerms={['GroupWrite']}>
                <Button
                  onClick={() => setActiveModalType(ModalType.PUBLISH)}
                  disabled={!isValid}
                  className="rounded-3xl px-6"
                >
                  Update
                </Button>
              </WithPermission>
            )}
            {type === GroupType.DRAFT && (
              <WithPermission requiredPerms={['DraftWrite']}>
                <Button onClick={handleSaveAsDraft} className="rounded-3xl px-6">
                  Save as draft
                </Button>
              </WithPermission>
            )}
          </div>
        </div>
      </div>
      {/* Modals */}
      {activeModalType && modals[activeModalType]}
    </>
  );
};

const CancelChangesModal: FC<CancelChangesModalProps> = ({ onSubmit, onCancel }) => {
  return (
    <Modal
      isOpen
      onClose={onCancel}
      header="Discard Employer Group changes?"
      actions={[
        { title: 'Cancel', kind: 'primary', autoClose: false, onClick: onCancel },
        {
          title: 'Yes, discard changes',
          kind: 'primary',
          autoClose: false,
          onClick: onSubmit
        }
      ]}
      size="small"
    >
      <p className="p-6 font-medium">
        You are about to discard changes to Employer Group. Are you sure you'd like to proceed?
      </p>
    </Modal>
  );
};

const DeleteDraftModal: FC<DeleteDraftModalProps> = ({ onSubmit, onCancel, groupName }) => {
  return (
    <Modal
      isOpen
      onClose={onCancel}
      header="Delete a draft of the group?"
      actions={[
        { title: 'Cancel', kind: 'secondary', autoClose: false, onClick: onCancel },
        {
          title: 'Yes, delete this group',
          kind: 'primary',
          autoClose: false,
          onClick: onSubmit
        }
      ]}
      size="small"
    >
      <p className="p-6 font-medium">
        You are about to delete {groupName} draft Are you sure you'd like to proceed and delete this group?
      </p>
    </Modal>
  );
};

const ConfirmPublishModal: FC<ConfirmPublishModalProps> = ({ onSubmit, onCancel, groupName }) => {
  return (
    <Modal
      isOpen
      onClose={onCancel}
      header="Are you sure you want to continue?"
      actions={[
        { title: 'Cancel', kind: 'secondary', autoClose: false, onClick: onCancel },
        {
          title: 'Continue',
          kind: 'primary',
          autoClose: false,
          onClick: onSubmit
        }
      ]}
      size="small"
    >
      <p className="p-6 font-medium">You are about to publish changes to {groupName}</p>
    </Modal>
  );
};

const TerminateModal: FC<TerminateModalProps> = ({ onSubmit, onCancel, data }) => {
  const [isConfirmed, setConfirmation] = useState<boolean>(false);
  const [bsName, endDate, groups] = data;

  const methods = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      end_dt: endDate,
      terminateReason: '',
      customReason: ''
    },
    resolver: joiResolver(terminateValidation)
  });

  const { watch, getValues, formState } = methods;

  const formsEndDate: Nullable<Date> = getValues('end_dt');
  const end_dt: string = formsEndDate ? format(new Date(formsEndDate), 'MM/dd/yyyy') : '';

  const handleTerminate = useCallback(() => {
    if (!isConfirmed) {
      setConfirmation(true);
    } else {
      onSubmit(getValues());
      onCancel();
    }
  }, [isConfirmed, onSubmit, getValues, onCancel]);

  return (
    <Modal
      isOpen
      onClose={onCancel}
      header="Terminate this group"
      actions={[
        {
          title: isConfirmed ? 'No, return to groups' : 'Cancel',
          kind: 'secondary',
          autoClose: false,
          onClick: onCancel
        },
        {
          title: 'Yes, terminate this group',
          kind: 'primary',
          isDisabled: () => !formState.isValid,
          autoClose: false,
          onClick: handleTerminate
        }
      ]}
      size={isConfirmed ? 'prompt' : 'medium'}
    >
      {isConfirmed ? (
        <div className="p-6">
          <h3>Are you sure you want to terminate this group?</h3>
          <p>
            You are about to cancel {groups?.map(({ name }) => name)} and all of its associated groups on {end_dt}
          </p>
        </div>
      ) : (
        <FormProvider {...methods}>
          <form className="p-6 w-full">
            <h3>You are about to terminate {bsName || 'this group'}. Are you sure you would like to proceed?</h3>
            <div className="grid grid-cols-4 gap-4 mt-5">
              <DatePicker
                name="end_dt"
                label="Termination Date"
                wrapperClasses="col-span-1"
                minDate={subDays(new Date(), 59)}
                required
              />
              <Select
                name="terminateReason"
                label="Reason for Termination"
                wrapperClasses="col-span-3"
                options={terminationReason}
                required
              />
            </div>
            {watch('terminateReason') === 'Other' && (
              <TextArea label="Notes" name="customReason" rows={5} maxLength={100} required />
            )}
          </form>
        </FormProvider>
      )}
    </Modal>
  );
};
